/**
 * /drill/fret-caged — CAGED Shapes (rung 4).
 *
 * Hear a chord function in key, see the 3rd + 5th in the shape area,
 * tap the root on the canonical root string. All five CAGED shapes.
 *
 * See docs/design/guitar-caged-v1.md and guitar-caged-v2.md.
 */

import { useState, useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  expandNode,
  MVP_NODES,
  EXERCISE_SKILL_NODES,
  type Item,
} from "@music-app/learning-system";
import { ModeSwitcherBar, MODE_COLOR, type ExerciseMode } from "../components/ModeSwitcherBar.js";
import { GuitarDrillCore, GuitarNodeStats } from "./FretboardExercise.js";
import { PageHeader } from "../components/PageHeader.js";
import { buttonClasses } from "../components/Button.js";

// ─── Item pool ────────────────────────────────────────────────────────────────

const NODE_IDS = EXERCISE_SKILL_NODES["fret-caged"];
const ALL_ITEMS: Item[] = NODE_IDS.flatMap((id) =>
  expandNode(MVP_NODES.find((n) => n.id === id)!),
);
const EA_ITEMS  = expandNode(MVP_NODES.find((n) => n.id === "fret-caged-shapes-ea")!);
const CGD_ITEMS = expandNode(MVP_NODES.find((n) => n.id === "fret-caged-shapes-cgd")!);

const CAGED_STAT_ROWS = [
  { nodeId: "fret-caged-shapes-ea",  label: "E + A shapes" },
  { nodeId: "fret-caged-shapes-cgd", label: "C + G + D shapes" },
] as const;

type QualityFilter = "maj" | "min" | "mixed";
type CagedShape = "E" | "A" | "G" | "C" | "D";

const QUALITIES: QualityFilter[] = ["maj", "min", "mixed"];
const CAGED_SHAPES: CagedShape[] = ["E", "A", "G", "C", "D"];

const QUALITY_LABELS: Record<QualityFilter, string> = { maj: "Major", min: "Minor", mixed: "Mixed" };
const CAGED_SHAPE_LABELS: Record<CagedShape, string> = {
  E: "E-shape", A: "A-shape", G: "G-shape", C: "C-shape", D: "D-shape",
};

const SESSION_LENGTHS = [5, 10, 15, 20, 30];
const DEFAULT_SESSION_LENGTH = 10;

// ─── Page ─────────────────────────────────────────────────────────────────────

export function FretCagedExercisePage(): ReactNode {
  const [searchParams] = useSearchParams();
  const rawMode = searchParams.get("mode") as ExerciseMode | null;
  const mode: Exclude<ExerciseMode, "challenge"> =
    rawMode === "listen" || rawMode === "review" ? rawMode : "practice";
  const from    = searchParams.get("from") ?? undefined;
  const coached = searchParams.get("coached") === "true";
  const nodeId  = searchParams.get("nodeId") ?? undefined;

  const [sessionLength, setSessionLength] = useState(DEFAULT_SESSION_LENGTH);
  const [qualityFilter, setQualityFilter] = useState<QualityFilter>("mixed");
  const [selectedShapes, setSelectedShapes] = useState<Set<CagedShape>>(new Set(CAGED_SHAPES));

  function toggleShape(s: CagedShape | "all") {
    if (s === "all") { setSelectedShapes(new Set(CAGED_SHAPES)); return; }
    setSelectedShapes((prev) => {
      const next = new Set(prev);
      if (next.has(s)) { next.delete(s); if (next.size === 0) return new Set(CAGED_SHAPES); }
      else { next.add(s); }
      return next;
    });
  }

  // When navigated from the plan for a specific node, restrict items to that node.
  const basePool = nodeId === "fret-caged-shapes-ea" ? EA_ITEMS
    : nodeId === "fret-caged-shapes-cgd" ? CGD_ITEMS
    : ALL_ITEMS;

  const nodeItems = useMemo(() => {
    let items = basePool;
    if (qualityFilter !== "mixed") items = items.filter((i) => i.params["quality"] === qualityFilter);
    if (selectedShapes.size < CAGED_SHAPES.length) items = items.filter((i) => selectedShapes.has(i.params["shape"] as CagedShape));
    return items.length > 0 ? items : ALL_ITEMS;
  }, [qualityFilter, selectedShapes, basePool]);

  const safeItems = nodeItems.length > 0 ? nodeItems : ALL_ITEMS;

  return (
    <GuitarDrillCore
      skillId="fret-caged"
      nodeItems={safeItems}
      exercisePath="/guitar/fret-caged"
      drillTitle="CAGED Shapes"
      mode={mode}
      nodeId={nodeId}
      from={from}
      coached={coached}
      sessionLength={sessionLength}
      renderSetupScreen={(onStart, ready) => (
        <SetupScreen
          mode={mode}
          from={from}
          qualityFilter={qualityFilter}
          onQualityFilter={setQualityFilter}
          selectedShapes={selectedShapes}
          onToggleShape={toggleShape}
          sessionLength={sessionLength}
          onSessionLength={setSessionLength}
          ready={ready}
          onStart={onStart}
        />
      )}
    />
  );
}

// ─── Setup screen ─────────────────────────────────────────────────────────────

function SetupScreen({
  mode,
  from,
  qualityFilter,
  onQualityFilter,
  selectedShapes,
  onToggleShape,
  sessionLength,
  onSessionLength,
  ready,
  onStart,
}: {
  mode: Exclude<ExerciseMode, "challenge">;
  from?: string;
  qualityFilter: QualityFilter;
  onQualityFilter: (q: QualityFilter) => void;
  selectedShapes: Set<CagedShape>;
  onToggleShape: (s: CagedShape | "all") => void;
  sessionLength: number;
  onSessionLength: (n: number) => void;
  ready: boolean;
  onStart: () => void;
}): ReactNode {
  const modeColor = MODE_COLOR[mode];
  const ACTIVE = { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" };
  const INACTIVE = { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" };
  const [showCustomLength, setShowCustomLength] = useState(false);
  const backLink =
    from === "today" ? { label: "← Today", href: "/" } :
    from === "learn" ? { label: "← Learn", href: "/learn" } :
    { label: "← Guitar Hub", href: "/guitar" };

  return (
    <div className="max-w-2xl mx-auto py-9 px-8 pb-16">
      <PageHeader
        back={backLink}
        title="CAGED Shapes"
        subhead="Hear a chord function — find the root of the named shape on the neck."
      />

      <ModeSwitcherBar current={mode} exercisePath="/guitar/fret-caged" />

      <GuitarNodeStats rows={CAGED_STAT_ROWS} />

      {/* Quality selector */}
      <section className="mb-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Quality
        </p>
        <div className="flex flex-wrap gap-2">
          {QUALITIES.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => onQualityFilter(q)}
              aria-pressed={qualityFilter === q}
              className="px-3 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              style={qualityFilter === q ? ACTIVE : INACTIVE}
            >
              {QUALITY_LABELS[q]}
            </button>
          ))}
        </div>
      </section>

      {/* Shape selector */}
      <section className="mb-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Shape
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onToggleShape("all")}
            aria-pressed={selectedShapes.size === CAGED_SHAPES.length}
            className="px-3 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
            style={selectedShapes.size === CAGED_SHAPES.length ? ACTIVE : INACTIVE}
          >
            All
          </button>
          {CAGED_SHAPES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onToggleShape(s)}
              aria-pressed={selectedShapes.has(s)}
              className="px-3 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              style={selectedShapes.has(s) ? ACTIVE : INACTIVE}
            >
              {CAGED_SHAPE_LABELS[s]}
            </button>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mb-3">
          Exercises per session
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          {SESSION_LENGTHS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => { onSessionLength(n); setShowCustomLength(false); }}
              aria-pressed={!showCustomLength && sessionLength === n}
              className="px-4 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              style={
                !showCustomLength && sessionLength === n
                  ? { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" }
                  : { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" }
              }
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setShowCustomLength(true)}
            aria-pressed={showCustomLength}
            className="px-4 py-1.5 font-mono text-xs border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
            style={showCustomLength ? { borderColor: modeColor, backgroundColor: modeColor, color: "#FAF6EE" } : { borderColor: "rgba(26,24,19,0.2)", backgroundColor: "#FAF6EE", color: "#1A1813" }}
          >
            Other…
          </button>
          {showCustomLength && (
            <input
              type="number"
              min={1}
              max={50}
              value={sessionLength}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v) && v >= 1 && v <= 50) onSessionLength(v);
              }}
              className="w-16 px-3 py-1.5 font-mono text-xs border border-ink/20 bg-paper text-ink text-center focus:outline-none focus:border-ink"
              aria-label="Custom number of exercises"
              autoFocus
            />
          )}
        </div>
      </section>

      <button
        type="button"
        onClick={onStart}
        disabled={!ready}
        aria-busy={!ready}
        className={`mt-8 ${buttonClasses("primary")}`}
        style={{ backgroundColor: MODE_COLOR[mode] }}
      >
        {ready ? "Start →" : "Loading sounds…"}
      </button>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded mt-4">
        {ready ? "Audio starts on tap." : "Preparing the instrument…"}
      </p>
    </div>
  );
}
